"use client";

import React, {
  CSSProperties,
  ComponentPropsWithoutRef,
  HTMLAttributes,
  ReactNode,
  memo,
} from "react";
import TextWithStrong from "./TextWithStrong";

export type TypeScale =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "subtitle1"
  | "subtitle2"
  | "subtitle3"
  | "body"
  | "body1"
  | "body2"
  | "button1"
  | "button2"
  | "button3"
  | "caption"
  | "overline";

export type TypographyProps = {
  variant?: TypeScale;
  component?: TypeScale | "p" | "span" | "div";
  children?: ReactNode | string;
  style?: CSSProperties;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
} & ComponentPropsWithoutRef<"div">;

// Exported so non-Typography elements (e.g. EditableTypography's <input>) can
// borrow the exact classes of a type scale.
export const typeScaleClassNames: {
  [key: string]: HTMLAttributes<HTMLElement>["className"];
} = {
  h1: "text-6xl md:text-7xl font-semibold font-display [&>*]:font-semibold [&>strong]:text-violet-500",
  h2: "text-5xl md:text-6xl font-semibold font-display [&>*]:font-semibold [&>strong]:text-violet-500",
  h3: "text-3xl md:text-4xl font-semibold font-display [&>*]:font-semibold [&>strong]:text-violet-500",
  h4: "text-2xl md:text-2xl font-semibold font-display [&>*]:font-semibold [&>strong]:text-violet-500",
  h5: "text-xl md:text-xl font-semibold font-display [&>*]:font-semibold [&>strong]:text-violet-500",
  h6: "text-lg md:text-lg font-semibold font-display [&>*]:font-semibold [&>strong]:text-violet-500 !leading-tight",
  subtitle1:
    "text-lg dark:font-normal font-medium md:leading-7 font-display [&>*]:font-medium dark:&>*]:font-normal",
  subtitle2: "text-base  font-medium font-display [&>*]:font-medium",
  subtitle3:
    "text-sm dark:font-normal font-medium font-display [&>*]:font-medium dark:[&>*]:font-normal",
  body: "text-xl font-sans font-display font-normal [&>*]:font-normal leading-relaxed",
  body1:
    "text-lg font-sans font-display font-normal [&>*]:font-normal leading-relaxed",
  body2: "text-base font-normal font-sans [&>*]:font-normal leading-relaxed",
  button1: "text-sm font-medium font-display [&>*]:font-medium",
  button2: "text-sm font-medium font-display [&>*]:font-medium",
  button3: "text-xs font-medium font-display [&>*]:font-medium",
  caption:
    "text-sm font-normal font-display [&>*]:font-normal [&>strong]:font-medium",
  overline: "text-xs font-normal font-sans [&>*]:font-normal",
};

const TypographyComponent = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant = "body1",
      component = variant,
      children,
      style,
      className,
      onClick,
      ...rest
    }: TypographyProps,
    ref,
  ) => {
    const _props = {
      style,
      className: `${typeScaleClassNames[variant]} ${className} font-display`,
      onClick,
      ...rest,
    };

    if (typeof children === "string" && children.includes("**")) {
      children = <TextWithStrong>{children}</TextWithStrong>;
    }

    switch (component) {
      case "h1":
        return (
          <h1 {..._props} ref={ref as React.Ref<HTMLHeadingElement>}>
            {children}
          </h1>
        );
      case "h2":
        return (
          <h2 {..._props} ref={ref as React.Ref<HTMLHeadingElement>}>
            {children}
          </h2>
        );
      case "h3":
        return (
          <h3 {..._props} ref={ref as React.Ref<HTMLHeadingElement>}>
            {children}
          </h3>
        );
      case "h4":
        return (
          <h4 {..._props} ref={ref as React.Ref<HTMLHeadingElement>}>
            {children}
          </h4>
        );
      case "h5":
        return (
          <h5 {..._props} ref={ref as React.Ref<HTMLHeadingElement>}>
            {children}
          </h5>
        );
      case "h6":
        return (
          <h6 {..._props} ref={ref as React.Ref<HTMLHeadingElement>}>
            {children}
          </h6>
        );
      case "subtitle1":
      case "subtitle2":
      case "subtitle3":
        return (
          <span {..._props} ref={ref as React.Ref<HTMLSpanElement>}>
            {children}
          </span>
        );
      case "body":
      case "body1":
      case "body2":
        return (
          <p {..._props} ref={ref as React.Ref<HTMLParagraphElement>}>
            {children}
          </p>
        );
      case "button1":
      case "button2":
      case "button3":
      case "overline":
        return (
          <span {..._props} ref={ref as React.Ref<HTMLSpanElement>}>
            {children}
          </span>
        );
      case "caption":
        return (
          <p {..._props} ref={ref as React.Ref<HTMLParagraphElement>}>
            {children}
          </p>
        );
      case "p":
        return (
          <p {..._props} ref={ref as React.Ref<HTMLParagraphElement>}>
            {children}
          </p>
        );
      case "span":
        return (
          <span {..._props} ref={ref as React.Ref<HTMLSpanElement>}>
            {children}
          </span>
        );
      case "div":
        return (
          <div {..._props} ref={ref as React.Ref<HTMLDivElement>}>
            {children}
          </div>
        );
      default:
        return <></>;
    }
  },
);

function arePropsEqual(prevProps: TypographyProps, nextProps: TypographyProps) {
  if (prevProps.variant !== nextProps.variant) return false;
  if (prevProps.component !== nextProps.component) return false;
  if (prevProps.children !== nextProps.children) return false;
  if (prevProps.style !== nextProps.style) return false;
  if (prevProps.className !== nextProps.className) return false;
  return true;
}

TypographyComponent.displayName = "TypographyComponent";

export default memo(TypographyComponent, arePropsEqual);
